import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useAuthOptional } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { normalizeSupportedLocale } from '../utils/routing/locale-from-pathname';
import { subscribeToMasterAlerts } from '../utils/email-alerts/subscribe-to-master-alerts';
import { shouldShowFollowEmailPrompt, markFollowEmailPromptSeen } from '../utils/engagement/follow-email-prompt';
import { trackEvent } from '../utils/analytics/analytics';

export interface AlertUpsellToastMessages {
  /** e.g. "Following {company}: want an email when they post new roles?" Already
   * interpolated by the caller, since only it knows the entity's display name. */
  offer: string;
  turnOn: string;
  enabled: string;
  pendingConfirmation: string;
  failed: string;
}

export interface UseAlertUpsellToastParams {
  /** Which follow action can trigger this offer, recorded on the analytics event
   * alongside the existing 'shown'/'accepted' action, so the two are never conflated. */
  entityType: 'company' | 'role';
  /** companyId or roleTitle, carried on the analytics event as company_id/role_title,
   * matching the field names the pre-extraction CompanyFollowButton event already used. */
  entityId: string;
  /** Where the follow button itself is placed on the page (e.g. 'job-page', 'srp-hit'),
   * distinct from entityType, and optional since RoleFollowButton doesn't have this concept yet. */
  surface?: string;
}

/**
 * The activation-wedge toast originally built for CompanyFollowButton.tsx's
 * maybeOfferEmailUpdates (TEC-514 #3): right after a first follow, a signed-in user without
 * active alert emails is offered them once. A follow is only a live subscription if its
 * updates can actually reach the user. Extracted so RoleFollowButton gets the identical flow
 * (status check, subscribe, pending/active/failed toast copy) rather than a re-implementation
 * that could quietly drift from the company version. Copy stays with each caller (component
 * JSX owns its own defineMessages, matching useAlertCtaCore's split); this hook only owns the
 * status-check/subscribe/cooldown mechanics.
 */
export function useAlertUpsellToast({ entityType, entityId, surface }: UseAlertUpsellToastParams) {
  const intl = useIntl();
  const { showToast } = useToast();
  const auth = useAuthOptional();

  const entityIdParams = useCallback(
    () => (entityType === 'company' ? { company_id: entityId } : { role_title: entityId }),
    [entityType, entityId]
  );

  const handleEnableEmailUpdates = useCallback(
    async (messages: AlertUpsellToastMessages) => {
      const locale = normalizeSupportedLocale(intl.locale);
      trackEvent({
        name: 'follow_email_prompt',
        params: { action: 'accepted', entity_type: entityType, surface: surface ?? 'unknown', locale: intl.locale, ...entityIdParams() },
      });
      try {
        await subscribeToMasterAlerts(locale);
        // Distinguish "on" from "waiting on email confirmation": the subscribe
        // endpoint puts unverified accounts into pendingConfirmation.
        let status: string | undefined;
        try {
          const res = await fetch('/api/alerts/subscription');
          if (res.ok) status = (await res.json())?.subscription?.status;
        } catch {
          // Status check is cosmetic; fall through to the generic confirmation.
        }
        showToast(status === 'pendingConfirmation' ? messages.pendingConfirmation : messages.enabled, 'success', 5000);
      } catch {
        showToast(messages.failed, 'error', 5000);
      }
    },
    [intl.locale, showToast, entityType, surface, entityIdParams]
  );

  /**
   * Returns true when the offer toast was shown (it doubles as the follow confirmation, so the
   * caller must skip its own standard "followed"/"watching" toast in that case).
   */
  const maybeOffer = useCallback(
    async (messages: AlertUpsellToastMessages): Promise<boolean> => {
      if (!auth?.isAuthenticated || !shouldShowFollowEmailPrompt()) return false;
      try {
        const res = await fetch('/api/alerts/subscription');
        if (!res.ok) return false;
        const status = (await res.json())?.subscription?.status;
        if (status === 'active') return false;
      } catch {
        return false;
      }
      markFollowEmailPromptSeen();
      trackEvent({
        name: 'follow_email_prompt',
        params: { action: 'shown', entity_type: entityType, surface: surface ?? 'unknown', locale: intl.locale, ...entityIdParams() },
      });
      showToast(messages.offer, 'success', 8000, {
        label: messages.turnOn,
        onClick: () => void handleEnableEmailUpdates(messages),
      });
      return true;
    },
    [auth?.isAuthenticated, intl.locale, showToast, entityType, surface, entityIdParams, handleEnableEmailUpdates]
  );

  return { maybeOffer };
}
